import { history } from 'nomst/Stores/Navigation/router.store';
import * as historyUtils from '@payscale/payscale-shared-ui/lib/HistoryUtils';

// This is put in to deal with PSP-23387. This number is well below the max
// value of a decimal in C# and it'll keep things from falling apart. In the long
// run we should consider doing a more thorough "did you mean to enter this number"
const MAX_ALLOWED_NUMBER: number = 1e+20;

export const formatCurrencyValue = (
    currency: string,
    value: number,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale?: string
): string => {

    // Euros are a special case because lab includes the country code in
    // in the currency??
    const isEuro = currency && currency.startsWith('EUR');
    value = value ? value : 0;

    const numberFormatOptions = {
        style: 'currency',
        currency: isEuro ? 'EUR' : currency,
        useGrouping: true,
        maximumFractionDigits,
        minimumFractionDigits
    } as Intl.NumberFormatOptions;

    return value.toLocaleString(locale, numberFormatOptions);
};

export const stripNonCurrency = (valueText: string): string => {
    return valueText.replace(/[^0-9.,]+/g, '');
};

export const formatPercentage = (
    value: number,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale?: string
): string => {
    return format(value, minimumFractionDigits, maximumFractionDigits, locale, 'percent');
};

export const formatHourly = (valueText: string) => {
    return `${valueText}/hour`;
};

export const formatDecimal = (
    value: number,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale?: string
): string => {
    return format(value, minimumFractionDigits, maximumFractionDigits, locale, 'decimal');
};

const format = (
    value: number,
    minimumFractionDigits: number,
    maximumFractionDigits: number,
    locale?: string,
    style?: string
): string => {

    value = value ? value : 0;
    const numberFormatOptions = {
        style,
        maximumFractionDigits,
        minimumFractionDigits
    } as Intl.NumberFormatOptions;

    return value.toLocaleString(locale, numberFormatOptions);
};

export const stripNonPercentage = (valueText: string): string => {
    return valueText.replace(/[^0-9.]+/g, '');
};

export const parseValueOrDefault = (valueText: string, defaultValue: number): number => {
    if (!valueText) {
        return defaultValue;
    }

    const value = parseNumber(valueText);
    return isNaN(value) ? defaultValue : value;
};

export const parseNumber = (valueText: string): number => {
    valueText = removeExtraPeriod(valueText);
    const value = valueText.replace(/[^0-9.]+/g, '');

    // If we're over the max just return the max.
    return Math.min(Number.parseFloat(value), MAX_ALLOWED_NUMBER);
};

const removeExtraPeriod = (valueText: string): string => {
    const firstPeriodIndex = valueText.indexOf('.');
    if (firstPeriodIndex === -1) {
        return valueText;
    }

    const integerText = valueText.substr(0, firstPeriodIndex);
    const fractionText = valueText.slice(firstPeriodIndex).replace(/\./g, '');
    return `${integerText}.${fractionText}`;
};

export const formatCurrencyValueAbbreviated = (
    currency: string,
    num: number,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
): string => {
    const availableAbbreviation = ['K', 'M', 'B', 'T'];
    const decimalPlaces = Math.pow(10, maximumFractionDigits);
    let abbreviation = '';
    for (let index = availableAbbreviation.length - 1; index >= 0; index--) {
        const size = Math.pow(10, (index + 1) * 3);

        if (size <= num) {
            num = Math.round(num * decimalPlaces / size) / decimalPlaces;

            if ((num === 1000) && (index < availableAbbreviation.length - 1)) {
                num = 1;
                index++;
            }

            abbreviation = availableAbbreviation[index];
            break;
        }
    }

    return formatCurrencyValue(currency, num, minimumFractionDigits, maximumFractionDigits) + abbreviation;
};

export const doesUrlHaveDebugParam = (url: string): boolean => {
    const queryParamsIndex = url.indexOf('?');
    const queryParamsUrl = url.substr(queryParamsIndex);
    const queryParams = getJsonFromUrl(queryParamsUrl);
    return !!queryParams.debug;
};

export const getJsonFromUrl = (value: string) => {
    const query = value.substr(1);
    const result: {[key: string]: string[] } = {};
    query.split('&').forEach((part) => {
        const [key, val] = part.split('=');
        const array = (result[key] || []);
        array.push(decodeURIComponent(val));
        result[key] = array;
    });
    return result;
};

export const getUrlFromCurrentBase = (subPath: string) => {
    const fullUrl = historyUtils.parsePath(window.location.href).origin;
    const relativeUrl = history.location.pathname;
    const baseUrl = fullUrl.replace(relativeUrl, '/');
    const divider = baseUrl.endsWith('/') ? '' : '/';
    return `${baseUrl}${divider}${subPath}`;
};

export const getFirstOnly = (s: string[]): string => {
    return (s || [] as string[])[0];
};